import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User, Tenant } from './types'

// ── Theme Store ──────────────────────────────────────────────────────
interface ThemeState {
  isDark: boolean
  toggleDark: () => void
  setDark: (dark: boolean) => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      isDark: false,
      toggleDark: () =>
        set((state) => {
          const next = !state.isDark
          document.documentElement.classList.toggle('dark', next)
          return { isDark: next }
        }),
      setDark: (dark: boolean) =>
        set(() => {
          document.documentElement.classList.toggle('dark', dark)
          return { isDark: dark }
        }),
    }),
    { name: 'lipaflow-theme' },
  ),
)

// ── Auth Store ───────────────────────────────────────────────────────

interface AuthState {
  // User and tenant data
  user: User | null
  tenant: Tenant | null

  // Auth state
  isAuthenticated: boolean
  authProvider: 'firebase'

  // Firebase user ID
  firebaseUid: string | null

  // Impersonation state
  isImpersonating: boolean
  impersonatedTenantName: string | null
  impersonationSessionType: 'sudo_user' | 'support_backdoor' | 'tenant_view_as' | null
  impersonationMode: 'full_access' | 'view_only' | null
  impersonatedTargetUserName: string | null
  originalTokens: { accessToken: string; refreshToken: string } | null

  // Legacy compat — impersonation still reads accessToken
  accessToken: string | null

  // Actions
  setFirebaseAuth: (firebaseUid: string) => void
  setUser: (user: User) => void
  setTenant: (tenant: Tenant | null) => void
  logout: () => void
  startImpersonation: (
    accessToken: string,
    tenantName: string,
    options?: {
      sessionType?: 'sudo_user' | 'support_backdoor' | 'tenant_view_as'
      mode?: 'full_access' | 'view_only'
      targetUserName?: string
    },
  ) => void
  stopImpersonation: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tenant: null,
      isAuthenticated: false,
      authProvider: 'firebase',
      firebaseUid: null,
      isImpersonating: false,
      impersonatedTenantName: null,
      impersonationSessionType: null,
      impersonationMode: null,
      impersonatedTargetUserName: null,
      originalTokens: null,
      accessToken: null,

      // Firebase auth
      setFirebaseAuth: (firebaseUid) =>
        set({
          firebaseUid,
          isAuthenticated: true,
          authProvider: 'firebase',
          accessToken: null,
        }),

      setUser: (user) => set({ user }),

      setTenant: (tenant) => set({ tenant }),

      logout: () =>
        set({
          user: null,
          tenant: null,
          isAuthenticated: false,
          authProvider: 'firebase',
          firebaseUid: null,
          isImpersonating: false,
          impersonatedTenantName: null,
          impersonationSessionType: null,
          impersonationMode: null,
          impersonatedTargetUserName: null,
          originalTokens: null,
          accessToken: null,
        }),

      startImpersonation: (accessToken, tenantName, options) => {
        const state = get()
        const alreadyImpersonating = state.isImpersonating
        set({
          originalTokens: alreadyImpersonating
            ? state.originalTokens
            : {
                accessToken: state.accessToken || '',
                refreshToken: '',
              },
          user: null,
          tenant: null,
          isAuthenticated: true,
          accessToken,
          isImpersonating: true,
          impersonatedTenantName: tenantName,
          impersonationSessionType: options?.sessionType || null,
          impersonationMode: options?.mode || null,
          impersonatedTargetUserName: options?.targetUserName || null,
        })
      },

      stopImpersonation: () => {
        const state = get()
        const restoredAccessToken = state.originalTokens?.accessToken || null
        const hasFirebaseSession = Boolean(state.firebaseUid)

        if (restoredAccessToken) {
          set({
            accessToken: restoredAccessToken,
            isImpersonating: false,
            impersonatedTenantName: null,
            impersonationSessionType: null,
            impersonationMode: null,
            impersonatedTargetUserName: null,
            originalTokens: null,
          })
        } else if (hasFirebaseSession) {
          set({
            accessToken: null,
            isImpersonating: false,
            impersonatedTenantName: null,
            impersonationSessionType: null,
            impersonationMode: null,
            impersonatedTargetUserName: null,
            originalTokens: null,
          })
        } else {
          set({
            user: null,
            tenant: null,
            isAuthenticated: false,
            authProvider: 'firebase',
            firebaseUid: null,
            accessToken: null,
            isImpersonating: false,
            impersonatedTenantName: null,
            impersonationSessionType: null,
            impersonationMode: null,
            impersonatedTargetUserName: null,
            originalTokens: null,
          })
        }
      },
    }),
    {
      name: 'lipaflow-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        user: state.user,
        tenant: state.tenant,
        isAuthenticated: state.isAuthenticated,
        authProvider: state.authProvider,
        firebaseUid: state.firebaseUid,
        isImpersonating: state.isImpersonating,
        impersonatedTenantName: state.impersonatedTenantName,
        impersonationSessionType: state.impersonationSessionType,
        impersonationMode: state.impersonationMode,
        impersonatedTargetUserName: state.impersonatedTargetUserName,
        originalTokens: state.originalTokens,
      }),
    }
  )
)
